/**
 * ChunkGeometry - Manages geometry lifecycle for voxel chunks
 *
 * Each chunk has three geometry slots (solid, transparent, liquid). The geometries are the
 * chunk's own CPU-side copy — never added to the scene: TerrainBatch copies each into its
 * (lod, layer) batch and draws from there. Nothing here is a THREE.Mesh; there is no per-chunk
 * object to cull, sort or submit.
 *
 * Preview mesh management lives in BuildPreview, not here.
 */

import * as THREE from 'three';
import { VOXEL_SCALE } from '@worldify/shared';
import { Chunk } from './Chunk.js';
import { SurfaceNetOutput } from './SurfaceNet.js';
import type { SplitSurfaceNetOutput } from './SurfaceNet.js';
import { expandGeometry, createBufferGeometry, resampleLightAttributes, type ExpandedMeshData } from './MeshGeometry.js';
import { LAYER_COUNT } from './LayerConfig.js';

// Re-export for external consumers
export type { ExpandedMeshData } from './MeshGeometry.js';

// ============== ChunkGeometry Class ==============

/**
 * Container for a chunk's geometries (solid, transparent, liquid).
 * Uses a slot-based architecture — all three layers are managed identically.
 */
export class ChunkGeometry {
  /** Per-layer geometry; null where the layer is empty. Handed to TerrainBatch by reference. */
  private layers: (THREE.BufferGeometry | null)[] = [null, null, null];

  /** Per-layer boundary (seam) vertex lists, for normal reconciliation. */
  private boundaries: (ExpandedMeshData['boundary'] | null)[] = [null, null, null];

  /**
   * Per-layer per-expanded-vertex grid cell index (SurfaceNets baseIdx into the GRID_SIZE³ grid).
   * Kept so light can be re-sampled without re-meshing (resampleLightFromGrid). Parallel to
   * each layer's vertex buffer; null when the layer is empty.
   */
  private cellIndices: (Uint16Array | null)[] = [null, null, null];

  /** The chunk this geometry represents */
  readonly chunk: Chunk;

  /** Whether the geometries have been disposed */
  disposed: boolean = false;

  /** Generation counter - increments each time geometry is updated */
  meshGeneration: number = 0;

  /**
   * INDEX currency, separate from `meshGeneration` — bumped when a layer's INDEX buffer is rewritten
   * in place while its vertices stay exactly where they are (the seam stitcher's band mask and its
   * restore, which collapse/uncollapse triangles: see SeamStitcher.maskBand/restoreBands).
   *
   * Why it cannot be `meshGeneration`. The apron cache signature is "what were these strips built
   * FROM", and it is read BEFORE the mask is applied and stored after — so when masking bumped
   * meshGeneration, the stored signature could never match the next flush's freshly-read one, and
   * every masked face rebuilt and re-uploaded its strips on EVERY flush (the apron churn that cache
   * exists to prevent). The two facts want different clocks: a strip's geometry depends on where the
   * vertices ARE (meshGeneration) and on which triangles are DRAWN (indexEpoch), and a mask only
   * moves the second.
   *
   * Consumers that need "is what I cached still what is on screen" key off BOTH (the debug
   * wireframe, the apron signature). Consumers that need "are these the same vertices" keep using
   * meshGeneration alone.
   */
  indexEpoch: number = 0;

  /** Layers whose normals / index buffer were rewritten in place since the batch last copied them. */
  private normalsDirty = 0;
  private indicesDirty = 0;

  /** The layers with rewritten normals, and forget them: the caller is about to copy exactly those. */
  takeNormalsDirty(): number {
    const m = this.normalsDirty;
    this.normalsDirty = 0;
    return m;
  }

  takeIndicesDirty(): number {
    const m = this.indicesDirty;
    this.indicesDirty = 0;
    return m;
  }

  /** A layer's index buffer was rewritten in place (a band mask or its restore). */
  markIndicesChanged(layer: number): void {
    this.indicesDirty |= 1 << layer;
    const idx = this.layers[layer]?.index;
    if (idx) idx.needsUpdate = true;
  }

  constructor(chunk: Chunk) {
    this.chunk = chunk;
  }

  // ============== Geometry Updates ==============

  /**
   * Create or update geometries from worker results (ExpandedMeshData).
   * Primary path — used by the async worker pipeline.
   */
  updateFromData(
    solid: ExpandedMeshData | null,
    transparent: ExpandedMeshData | null,
    liquid: ExpandedMeshData | null,
  ): void {
    this.updateAllSlots([solid, transparent, liquid]);
    this.disposed = false;
    this.meshGeneration++;
  }

  /**
   * Create or update geometries from SurfaceNetOutput (sync/fallback path).
   */
  updateFromSurfaceNet(output: SplitSurfaceNetOutput): void {
    const scale = VOXEL_SCALE * (1 << this.chunk.lod);
    this.updateAllSlots([
      toExpandedData(output.solid, scale),
      toExpandedData(output.transparent, scale),
      toExpandedData(output.liquid, scale),
    ]);
    this.disposed = false;
    this.meshGeneration++;
  }

  // ============== Accessors ==============

  /** Per-layer geometries, indexed by layer — what TerrainBatch.updateChunk copies from. */
  getGeometries(): (THREE.BufferGeometry | null)[] {
    return this.layers;
  }

  /** Check if any layer has geometry. Explicit, not `.some`: this is the seam path's display
   *  predicate and the LOD audit's, called thousands of times a flush, and a callback form allocates
   *  a closure per call to do three null checks. */
  hasGeometry(): boolean {
    if (this.disposed) return false;
    const l = this.layers;
    return l[0] !== null || l[1] !== null || l[2] !== null;
  }

  // ============== Stats ==============

  getVertexCount(): number {
    let count = 0;
    for (let i = 0; i < LAYER_COUNT; i++) {
      const attr = this.layers[i]?.getAttribute('position');
      if (attr) count += attr.count;
    }
    return count;
  }

  getTriangleCount(): number {
    let count = 0;
    for (let i = 0; i < LAYER_COUNT; i++) {
      const idx = this.layers[i]?.index;
      if (idx) count += idx.count / 3;
    }
    return count;
  }

  // ============== Disposal ==============

  /** Dispose all geometries */
  dispose(): void {
    for (let i = 0; i < this.layers.length; i++) {
      const g = this.layers[i];
      if (g) {
        g.dispose();
        this.layers[i] = null;
      }
      this.boundaries[i] = null;
      this.cellIndices[i] = null;
    }
    this.disposed = true;
  }

  // ============== Private Helpers ==============

  /** Update all layer slots from a data array. */
  private updateAllSlots(data: [ExpandedMeshData | null, ExpandedMeshData | null, ExpandedMeshData | null]): void {
    for (let i = 0; i < LAYER_COUNT; i++) {
      this.layers[i] = this.updateSlot(this.layers[i], data[i]);
      this.boundaries[i] = data[i]?.boundary ?? null;
      this.cellIndices[i] = data[i]?.cellIndices ?? null;
    }
  }

  // ============== Light-only re-sample (no re-mesh) ==============

  /**
   * Re-read this chunk's per-vertex light from an expanded GRID_SIZE³ grid and rewrite ONLY the
   * `lightLevel` + `blockLight` attributes in place — no SurfaceNets, no geometry realloc, no
   * geometry rebuild. Valid only when the chunk's voxels are unchanged (so the mesh, and
   * thus the stored `cellIndices`, still describe the geometry); the caller guarantees this by
   * routing voxel-changed chunks through a full remesh instead.
   *
   * The grid MUST be expanded exactly as the mesh consumed it (expandChunkToGrid), so each stored
   * cell index still points at the same 8 corners. Uses the shared sampleCellLight() so the result
   * is bit-identical to what a full remesh would bake.
   *
   * @returns true if any layer's light attributes were rewritten.
   */
  resampleLightFromGrid(grid: Uint32Array): boolean {
    let changed = false;
    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const cells = this.cellIndices[layer];
      const geo = this.layers[layer];
      if (!cells || !geo) continue;
      if (resampleLightAttributes(geo, cells, grid)) changed = true;
    }
    return changed;
  }

  // ============== Seam-normal reconciliation accessors ==============

  /** Boundary (seam) vertex lists for a layer, or null if the layer is empty. */
  getBoundary(layer: number): ExpandedMeshData['boundary'] | null {
    return this.boundaries[layer];
  }

  /** The layer's geometry (for reading positions and reading/writing normals). */
  getLayerGeometry(layer: number): THREE.BufferGeometry | null {
    return this.layers[layer];
  }

  /**
   * Per-vertex GRID CELL of a layer's expanded geometry — the flat GRID_SIZE³ index it was solved in.
   *
   * Kept for the light re-sample, and read by the seam stitcher to answer "how far from the chunk's
   * face is this vertex, in CELLS?". That question cannot be answered from the position alone: a cell's
   * vertex sits anywhere inside it, so a distance in metres does not tell you which cell you are in,
   * and the cross-LOD ownership rule is stated in exact cell counts (a coarse cell is exactly 2^k fine
   * cells) precisely so that it needs no tolerance.
   */
  getCellIndices(layer: number): Uint16Array | null {
    return this.cellIndices[layer];
  }

  /**
   * Flag a layer's normal attribute as edited. The chunk's own geometry is never uploaded (the
   * batch draws its copy), so the flag is bookkeeping; the write that reaches the screen is the
   * stitcher's `refreshChunk` notification.
   */
  markNormalsNeedUpdate(layer: number): void {
    this.normalsDirty |= 1 << layer;
    const attr = this.layers[layer]?.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (attr) attr.needsUpdate = true;
  }

  /**
   * Flag a layer's POSITION attribute as edited. Normals are flagged with it, because every writer
   * that moves a vertex takes its normal along.
   *
   * NOTHING IN THE SEAM PATH CALLS THIS ANY MORE, and that is the point: the cross-LOD weld that did
   * was deleted in phase 7 (it merged border vertices and tore same-LOD seams — see SeamStitcher's
   * header), so the only remaining in-place vertex writes are the mesher's own. Kept because the
   * plumbing is correct and the next legitimate in-place edit will want it.
   *
   * The bounding box/sphere are NOT recomputed: `createBufferGeometry` sets a conservative box over
   * the whole [0, CHUNK_SIZE + MESH_MARGIN] grid, so any in-grid displacement stays inside it.
   */
  markPositionsNeedUpdate(layer: number): void {
    const geo = this.layers[layer];
    if (!geo) return;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (pos) pos.needsUpdate = true;
    const nrm = geo.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (nrm) nrm.needsUpdate = true;
  }

  /** Replace one layer's geometry from data (disposing the old one), or clear it. */
  private updateSlot(existing: THREE.BufferGeometry | null, data: ExpandedMeshData | null): THREE.BufferGeometry | null {
    if (existing) existing.dispose();
    if (!data) return null;
    return createBufferGeometry(data, this.chunk.lod);
  }
}

// ============== Helper Functions ==============

/** Convert SurfaceNetOutput to ExpandedMeshData (or null if empty) */
function toExpandedData(output: SurfaceNetOutput, scale: number = VOXEL_SCALE): ExpandedMeshData | null {
  if (output.vertexCount === 0) return null;
  return expandGeometry(output, scale);
}
